import React, { useMemo } from 'react';
import Immutable from 'immutable';
import { Container, Image, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import { useTranslation } from 'react-i18next';
import config from 'react-global-configuration';
import type {
  ImmutableEntity,
  NodeEntity,
  ReferenceField,
  ProfileEntity,
} from '@uncinc/react-drupal-core/types';

import {
  Paragraph,
  Node,
  View,
} from '@uncinc/react-drupal-core';

import {
  Button,
  Card,
  ThemeHeader,
  WysiwygField,
  NewsletterBlock,
} from '../../../../components';

import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  SCREEN_WIDE,
} from '../../../../config/constants';

import type { ThemeProps } from '..';

import './style.scss';

interface ExpertsProps {
  experts: Immutable.List<ImmutableEntity<ProfileEntity>>;
}

const Experts: React.FC<ExpertsProps> = ({ experts }) => {
  const { t } = useTranslation();

  return (
    <div className="TaxonomyTheme__sidebar__experts">
      <h2 className="TaxonomyTheme__sidebar__experts__title">{t('tags.plural.expert')}</h2>
      {experts.map((expert) => (
        <Node
          viewMode="teaser"
          key={expert.get('uuid')}
          id={expert.getIn(['entity', 'nid', 0, 'value'])}
          entity={expert.getIn(['entity'])}
        />
      ))}
    </div>
  );
};

const TaxonomyTheme: React.FC<ThemeProps> = ({ entity }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = useMemo(() => width >= SCREEN_NORMAL, [width]);

  const language = config.get('DEFAULT_LANGUAGE');
  const activeDomain = config.get('CURRENT_DOMAIN_DRUPAL_IDENTIFIER');

  const id = entity.getIn(['tid', 0, 'value']);

  const hero = entity.getIn(['field_hero_image', 0]);
  const heroAlt = entity.getIn(['field_hero_image', 0, 'alt']);
  const title = entity.getIn(['name', 0, 'value'], '');
  const description = entity.getIn(['description', 0, 'processed'], '');
  const heroMobile = entity.getIn(['field_hero_image', 0, 'styles', 'hero_mobile']);
  const heroTablet = entity.getIn(['field_hero_image', 0, 'styles', 'hero_tablet']);
  const heroDesktop = entity.getIn(['field_hero_image', 0, 'styles', 'hero_desktop']);
  const paragraphs = entity.getIn(['field_paragraphs'], Immutable.List());

  const promotedContentBody = entity.getIn(['field_promoted_description', 0, 'processed'], '');
  const promotedContent = entity.getIn(['field_promoted_content'], Immutable.List());
  const allContentPath = `${entity.getIn(['paths', language])}/kennisaanbod`;

  const visionDescription = entity.getIn(['field_vision_description', 0, 'processed']);
  const visionLink = entity.getIn(['field_vision_link', 0, 'alias']);
  const visionImage = entity.getIn(['field_vision_image', 0, 'styles'], Immutable.Map());
  const visionImageAlt = entity.getIn(['field_vision_image', 0, 'alt']);

  const showSubjects = entity.getIn(['field_show_subjects', 0, 'value'], false);
  const experts = entity.get('field_experts', Immutable.List) as unknown as ExpertsProps['experts'];

  return (
    <div className="TaxonomyTheme">
      <div className="TaxonomyTheme__hero">
        {hero && (
          <Image
            lazyload={false}
            alt={heroAlt}
            src={{
              [SCREEN_MOBILE]: heroMobile,
              [SCREEN_TABLET]: heroTablet,
              [SCREEN_NORMAL]: heroDesktop,
            }}
          />
        )}
      </div>
      <Container>
        <div className="row">
          <div className="col-12 col-lg-8">
            <div className="TaxonomyTheme__TopVisual" />
            <div className="TaxonomyTheme__content">
              <ThemeHeader theme={entity} withTitle />

              {description ? (
                <div className="TaxonomyTheme__body__wrapper">
                  <WysiwygField
                    property="schema:text"
                    className="TaxonomyTheme__body"
                    html={description}
                  />
                </div>
              ) : null}
              {showSubjects && (
                <div className="TaxonomyTheme__Subjects">
                  <div className="row">
                    <div className="col-12">
                      <View id="vocabulary_terms" display="subject_theme" args={[id, activeDomain]} title={t('implementations.taxonomy.subjects', { theme: title })} />
                    </div>
                  </div>
                </div>
              )}
              <div className="TaxonomyTheme__Paragraphs">
                {
                  paragraphs.map((item) => {
                    const paragraphId = item.get('id');
                    const paragraph = item.getIn(['entity']);

                    return (
                      <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                    );
                  })
                }
              </div>
            </div>
          </div>
          {(experts && experts.size > 0 && isDesktop) ? (
            <div className="col-12 col-lg-3">
              <div className="TaxonomyTheme__sidebar">
                <Experts experts={experts} />
              </div>
            </div>
          ) : null}
        </div>
      </Container>
      <Container>
        <div className="TaxonomyTheme__PromotedContent">
          <div className="row">
            <div className="col-12 col-lg-8">
              <div className="TaxonomyTheme__PromotedContent__text">
                <h2>{t('implementations.taxonomy.promoted_content')}</h2>
                {promotedContentBody ? (
                  <div className="TaxonomyTheme__PromotedContent__body__wrapper">
                    <WysiwygField
                      property="schema:text"
                      className="TaxonomyTheme__PromotedContent__body"
                      html={promotedContentBody}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="TaxonomyTheme__PromotedContent__contents col-12">
              {promotedContent && (
                <div className="TaxonomyTheme__PromotedContent__contents__items">
                  {
                    promotedContent.map((
                      item: ImmutableEntity<ReferenceField<ImmutableEntity<NodeEntity>>>,
                    ) => {
                      const itemEntity = item.get('entity');

                      if (itemEntity) {
                        const uuid = itemEntity.getIn(['entity', 'uuid', 0, 'value']);
                        const itemType = itemEntity.getIn(['type', 0, 'target_id']);
                        const itemTitle = itemEntity.getIn(['title', 0, 'value']);
                        const summary = itemEntity.getIn(['summary', 0], itemEntity.getIn(['body', 0, 'summary'], itemEntity.getIn(['body', 0, 'processed'], itemEntity.getIn(['body', 0], itemEntity.getIn(['term_summary', 0])))));
                        const url = itemEntity.getIn(['paths', language]);
                        const image = itemEntity.getIn(['field_media_image', 0, 'styles', 'content_cards'], null);
                        const imageAlt = itemEntity.getIn(['field_media_image', 0, 'alt'], null);

                        return (
                          <Card
                            key={uuid}
                            title={itemTitle}
                            body={summary}
                            image={image}
                            alt={imageAlt}
                            type={itemType}
                            subType={itemType}
                            link={url}
                            small={false}
                          />
                        );
                      }

                      return null;
                    })
                  }
                </div>
              )}
              <Button
                type="inverted"
                href={allContentPath}
              >
                {t('implementations.taxonomy.all_promoted_content')} <i className="icon icon-arrow-right" />
              </Button>
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div className="TaxonomyTheme__Pillars">
          <div className="row">
            <div className="col-12">
              <View id="vocabulary_terms" display="pillars" link={allContentPath} />
            </div>
          </div>
        </div>
      </Container>
      <div className="TaxonomyTheme__Vision">
        <Container>
          <div className="TaxonomyTheme__Vision__Contents">
            <div className="TaxonomyTheme__Vision__Contents__text">
              <h2>{t('implementations.taxonomy.vision_title')}</h2>
              {visionDescription && (
                <div className="TaxonomyTheme__Vision__Contents__body">
                  <WysiwygField
                    property="schema:text"
                    className="TaxonomyTheme__Vision__body__text"
                    html={visionDescription}
                  />
                </div>
              )}
              {visionLink && (
                <Button
                  type="inverted"
                  href={visionLink}
                >
                  {t('implementations.taxonomy.vision_link')} <i className="icon icon-arrow-right" />
                </Button>
              )}
            </div>
            <div className="TaxonomyTheme__Vision__Contents__image">
              {(visionImage && visionImage.size > 0) && (
                <Image
                  alt={visionImageAlt}
                  src={{
                    [SCREEN_MOBILE]: visionImage.get('ratio_1_1_xs'),
                    [SCREEN_TABLET]: visionImage.get('ratio_1_1_sm'),
                    [SCREEN_NORMAL]: visionImage.get('ratio_1_1_md'),
                    [SCREEN_WIDE]: visionImage.get('ratio_1_1_xl'),
                  }}
                />
              )}
            </div>
          </div>
        </Container>
      </div>
      <Container>
        <div className="TaxonomyTheme__Workfields">
          <div className="row">
            <div className="col-12">
              <View id="vocabulary_terms" display="workfield_theme" args={[id]} title={t('implementations.taxonomy.workfields', { theme: title })} />
            </div>
          </div>
        </div>
      </Container>
      {(experts && experts.size > 0 && !isDesktop) && (
        <Container>
          <div className="row">
            <div className="col-12 mb-5">
              <Experts experts={experts} />
            </div>
          </div>
        </Container>
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </div>
  );
};

export default TaxonomyTheme;
